import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { FileText, Zap, Users, ShieldCheck, Star, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/Button';
export function HeroSection() {
  return <section className="relative pt-6 pb-10 md:pt-20 md:pb-32 px-3 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden" data-id="element-129">
      {/* Background glow effects */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-magenta-500/20 blur-[120px] rounded-full pointer-events-none" data-id="element-130" />
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gold-500/10 blur-[100px] rounded-full pointer-events-none" data-id="element-131" />

      <div className="flex flex-col items-center text-center relative z-10" data-id="element-132">
        {/* Badge */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="inline-flex items-center space-x-2 bg-navy-800/80 border border-gold-500/30 px-3 py-1.5 rounded-full text-gold-400 font-semibold text-xs sm:text-sm tracking-wide backdrop-blur-sm mb-4 md:mb-6" data-id="element-133">
          <Star className="w-3 h-3 sm:w-4 sm:h-4 fill-gold-400" data-id="element-134" />
          <span data-id="element-135">Sistema N°1 para Profesores en Argentina</span>
        </motion.div>

        {/* Title — "Artística Resuelto" stays on one line */}
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="font-black leading-[1.1] text-white max-w-4xl" data-id="element-136">
          <span className="block text-[1.35rem] sm:text-4xl md:text-5xl lg:text-6xl" data-id="element-137">
            Todo tu año de Gimnasia
          </span>
          <span className="block text-[1.55rem] sm:text-4xl md:text-5xl lg:text-6xl" data-id="element-138">
            Artística{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-500" data-id="element-139">
              Resuelto
            </span>
          </span>
        </motion.h1>

        {/* Product Image — between title and subtitle */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="relative my-4 md:my-8 w-[67%] max-w-xl" data-id="element-140">
          <div className="relative z-10 drop-shadow-2xl" data-id="element-141">
            <img src="https://cdn.magicpatterns.com/uploads/ppZYcyW5tAwH6w3KZmiGYG/gim_artistic-Photoroom.png" alt="Gimnasia Artística Resuelta Megapack" className="w-full h-auto object-contain" data-id="element-142" />
          </div>

          {/* Floating badge */}
          <motion.div animate={{
          y: [0, -10, 0]
        }} transition={{
          repeat: Infinity,
          duration: 4,
          ease: 'easeInOut'
        }} className="absolute -bottom-4 -right-4 sm:bottom-6 sm:-right-6 bg-navy-800 border border-gold-500/50 p-3 sm:p-4 rounded-xl sm:rounded-2xl shadow-xl backdrop-blur-md z-20 hidden sm:block" data-id="element-143">
            <div className="flex items-center space-x-3" data-id="element-144">
              <div className="bg-gold-500/20 p-2 rounded-full" data-id="element-145">
                <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-gold-400" data-id="element-146" />
              </div>
              <div data-id="element-147">
                <p className="text-white font-bold text-sm" data-id="element-148">
                  6 Tomos + 6 Bonos
                </p>
                <p className="text-xs text-gray-400" data-id="element-149">Envío digital inmediato</p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Subtitle */}
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="sm:text-lg md:text-xl text-gray-300 max-w-2xl mb-4 md:mb-6 text-[12px]" data-id="element-150">
          Tu año completo de clases de gimnasia artística listo: suelo, barra,
          potro, barras asimétricas, planificación por niveles, gestión y mucho
          más.{' '}
          <strong className="text-white text-[12px]" data-id="element-151">
            Sin improvisar nunca más.
          </strong>
        </motion.p>

        {/* Feature badges — inline row */}
        <div className="flex items-center justify-center gap-1 sm:gap-3 py-1 md:py-3 text-gray-400" data-id="element-152">
          {[{
          icon: FileText,
          text: 'PDF digital'
        }, {
          icon: Zap,
          text: 'Acceso inmediato'
        }, {
          icon: Users,
          text: 'Todos los niveles'
        }, {
          icon: ShieldCheck,
          text: 'Garantía 7 días'
        }].map((item, i) => <Fragment key={i} data-id="element-153">
              {i > 0 && <span className="text-navy-600 text-[8px] sm:text-xs" data-id="element-154">·</span>}
              <span className="flex items-center gap-0.5 sm:gap-1 text-[9px] sm:text-sm text-gray-300 whitespace-nowrap" data-id="element-155">
                <item.icon className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-gold-400" data-id="element-156" />
                {item.text}
              </span>
            </Fragment>)}
        </div>

        {/* CTA */}
        <div className="space-y-3 mt-4 md:mt-6 w-full max-w-lg" data-id="element-157">
          <Button className="w-full text-sm sm:text-lg py-3 sm:py-5 px-6 sm:px-10" data-id="element-158">
            💥 QUIERO MI GIMNASIA ARTÍSTICA RESUELTA
          </Button>

          <div className="flex items-center justify-center space-x-2 text-xs sm:text-sm text-gray-400" data-id="element-159">
            <ShieldCheck className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" data-id="element-160" />
            <span data-id="element-161">
              Pago 100% seguro · Mercado Pago | Tarjeta | Transferencia
            </span>
          </div>
        </div>

        {/* Social Proof Mini */}
        <div className="flex flex-wrap items-center justify-center gap-3 pt-4 mt-4 border-t border-navy-800 w-full max-w-lg" data-id="element-162">
          <div className="flex -space-x-2 sm:-space-x-3" data-id="element-163">
            {['https://randomuser.me/api/portraits/women/44.jpg', 'https://randomuser.me/api/portraits/women/68.jpg', 'https://randomuser.me/api/portraits/men/32.jpg', 'https://randomuser.me/api/portraits/women/25.jpg'].map((src, i) => <img key={i} src={src} alt={`Profesor ${i + 1}`} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-navy-900 object-cover" data-id="element-164" />)}
          </div>
          <div className="flex flex-col" data-id="element-165">
            <div className="flex items-center space-x-1" data-id="element-166">
              {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-3 h-3 sm:w-4 sm:h-4 fill-gold-400 text-gold-400" data-id="element-167" />)}
              <span className="text-white font-bold ml-1 sm:ml-2 text-sm" data-id="element-168">
                4.9/5
              </span>
            </div>
            <span className="text-xs sm:text-sm text-gray-400" data-id="element-169">
              +300 profes confían · 🇦🇷 Todo el país
            </span>
          </div>
        </div>
      </div>
    </section>;
}